import React, { useEffect, useRef, useState } from "react";
import { FileDown, Mail, LinkIcon, Unlink, Loader2, Image as ImageIcon, Upload, Trash2, FileText, CalendarClock } from "lucide-react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { exportExpensesCsvUrl, gmailConnectUrl, gmailDisconnect, gmailStatus, uploadOrgLogo, removeOrgLogo } from "@/lib/api";
import api from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { SETTINGS } from "@/constants/testIds";

export default function Settings() {
  const { user, org, refresh } = useAuth();
  const { toast } = useToast();
  const [gm, setGm] = useState(null);
  const [working, setWorking] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [sched, setSched] = useState({ enabled: false, day_of_month: 1, recipients: [], last_sent_at: null });
  const [schedRecipientText, setSchedRecipientText] = useState("");
  const [schedBusy, setSchedBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => gmailStatus().then(setGm);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    let cancelled = false;
    api.get("/api/orgs/me/board-pack-schedule").then((r) => {
      if (cancelled) return;
      setSched(r.data);
      setSchedRecipientText((r.data.recipients || []).join("\n"));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const saveSchedule = async () => {
    try {
      setSchedBusy(true);
      const recipients = schedRecipientText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      const r = await api.put("/api/orgs/me/board-pack-schedule", {
        enabled: sched.enabled,
        day_of_month: Number(sched.day_of_month) || 1,
        recipients,
      });
      setSched(r.data);
      toast({ title: "Schedule saved" });
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setSchedBusy(false); }
  };

  const sendNow = async () => {
    try {
      setSchedBusy(true);
      const r = await api.post("/api/orgs/me/board-pack/send-now");
      const count = r.data.count || 0;
      toast({
        title: count > 0 ? `Board pack emailed to ${count} recipient${count === 1 ? "" : "s"}` : "Saved — Gmail not configured",
        description: count > 0 ? r.data.sent_to.join(", ") : "Connect Gmail above to enable real sending. (PDF was generated but no email backend.)",
      });
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setSchedBusy(false); }
  };

  const onPickLogo = () => fileRef.current?.click();
  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Pick an image file", variant: "destructive" });
      return;
    }
    if (file.size > 600 * 1024) {
      toast({ title: "Logo must be ≤ 600 KB", description: "Try a 256×256 PNG.", variant: "destructive" });
      return;
    }
    setLogoBusy(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await uploadOrgLogo(dataUrl);
      await refresh();
      toast({ title: "Logo updated" });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail, variant: "destructive" });
    } finally { setLogoBusy(false); }
  };

  const deleteLogo = async () => {
    if (!window.confirm("Remove organization logo?")) return;
    setLogoBusy(true);
    try {
      await removeOrgLogo();
      await refresh();
      toast({ title: "Logo removed" });
    } finally { setLogoBusy(false); }
  };

  const download = (flavor) => {
    const tok = localStorage.getItem("td_session");
    fetch(exportExpensesCsvUrl(flavor), { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => r.blob())
      .then((b) => {
        const u = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = u;
        a.download = `trustdime_expenses_${flavor}.csv`;
        a.click();
        URL.revokeObjectURL(u);
      });
  };

  const connectGmail = async () => {
    setWorking(true);
    try {
      const r = await gmailConnectUrl();
      window.location.href = r.url;
    } catch (err) {
      toast({ title: "Cannot connect", description: err?.response?.data?.detail, variant: "destructive" });
      setWorking(false);
    }
  };

  const disconnectGmail = async () => {
    try {
      await gmailDisconnect();
      toast({ title: "Gmail disconnected" });
      await load();
    } catch (err) {
      toast({ title: "Could not disconnect", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  return (
    <>
      <PageHeader title="Settings" subtitle="Connections, exports, and stewardship knobs." />
      <PageBody>
        <div className="space-y-8 max-w-3xl">
          {/* Organization Logo */}
          <section className="border border-border bg-card rounded-md p-6" data-testid="org-logo-section">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-md bg-accent flex items-center justify-center">
                <ImageIcon size={18} className="text-primary" />
              </div>
              <div className="flex-1">
                <div className="serif text-lg text-primary">Organization logo</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Shown in the top-left of the navigation panel and on the donor page. PNG or SVG, up to 600 KB. Square images work best.
                </p>
                <div className="mt-5 flex items-center gap-5">
                  <div
                    className="h-20 w-20 rounded-lg border-2 border-dashed border-border bg-background flex items-center justify-center overflow-hidden"
                    data-testid="org-logo-preview"
                  >
                    {org?.logo_data_url ? (
                      <img src={org.logo_data_url} alt={org.name} className="h-full w-full object-cover" />
                    ) : (
                      <ImageIcon size={24} className="text-muted-foreground/40" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      onChange={onLogoFile}
                      className="hidden"
                      data-testid="org-logo-file"
                    />
                    <button
                      onClick={onPickLogo}
                      disabled={logoBusy}
                      data-testid="org-logo-upload"
                      className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-60"
                    >
                      {logoBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      {org?.logo_data_url ? "Replace" : "Upload"} logo
                    </button>
                    {org?.logo_data_url && (
                      <button
                        onClick={deleteLogo}
                        disabled={logoBusy}
                        data-testid="org-logo-remove"
                        className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:text-destructive hover:border-destructive/40"
                      >
                        <Trash2 size={14} /> Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Board Pack monthly schedule */}
          <section className="border border-border bg-card rounded-md p-6" data-testid="board-pack-schedule">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-md bg-accent flex items-center justify-center">
                <CalendarClock size={18} className="text-primary" />
              </div>
              <div className="flex-1">
                <div className="serif text-lg text-primary">Schedule monthly Board Pack</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Auto-generate and email a one-page PDF (KPIs, programs, utilization) on a fixed day each month. Works with the same Gmail connection below.
                  {sched.last_sent_at && <span className="block mt-1"><strong>Last sent:</strong> {new Date(sched.last_sent_at).toLocaleString()}</span>}
                </p>
                <div className="mt-5 space-y-3 text-sm">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={sched.enabled} onChange={(e) => setSched({ ...sched, enabled: e.target.checked })} data-testid="board-pack-enabled" />
                    <span>Enable monthly Board Pack delivery</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Day of month</label>
                    <select value={sched.day_of_month} onChange={(e) => setSched({ ...sched, day_of_month: Number(e.target.value) })}
                      data-testid="board-pack-day"
                      className="px-3 py-1.5 rounded border border-border bg-background text-sm">
                      {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}{d === 1 ? "st" : d === 2 ? "nd" : d === 3 ? "rd" : "th"}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Recipients</label>
                    <textarea
                      data-testid="board-pack-recipients"
                      rows={3}
                      value={schedRecipientText}
                      onChange={(e) => setSchedRecipientText(e.target.value)}
                      placeholder="board@example.org&#10;treasurer@example.org"
                      className="w-full mt-1 px-3 py-2 rounded border border-border bg-background font-mono text-xs"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">One email per line or comma-separated. Max 20.</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={saveSchedule} disabled={schedBusy} data-testid="board-pack-save"
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium">
                      {schedBusy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                      Save schedule
                    </button>
                    <button onClick={sendNow} disabled={schedBusy} data-testid="board-pack-send-now"
                      className="inline-flex items-center gap-1.5 px-4 py-2 border border-border rounded-md text-sm hover:bg-accent">
                      Send Board Pack now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Gmail */}
          <section data-testid={SETTINGS.gmailStatus} className="border border-border bg-card rounded-md p-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-md bg-accent flex items-center justify-center">
                <Mail size={18} className="text-primary" />
              </div>
              <div className="flex-1">
                <div className="serif text-lg text-primary">Notification email (Gmail)</div>
                <p className="text-sm text-muted-foreground mt-1">
                  When connected, TrustDime sends expense-state notifications to volunteers
                  <em> from your org&apos;s Gmail address</em>.
                </p>
                {!gm?.available ? (
                  <div className="mt-4 rounded-sm border border-secondary/40 bg-secondary/10 text-foreground px-4 py-3 text-sm">
                    Gmail integration isn&apos;t fully configured on this server yet. Ask your administrator
                    to add <span className="mono">GOOGLE_CLIENT_ID</span>, <span className="mono">GOOGLE_CLIENT_SECRET</span>
                    , and <span className="mono">BACKEND_PUBLIC_URL</span> to <span className="mono">backend/.env</span>.
                    See <span className="mono">/app/memory/test_credentials.md</span>.
                  </div>
                ) : gm.connected ? (
                  <div className="mt-4 flex items-center justify-between gap-3 rounded-sm border border-status-approved/30 bg-status-approved/10 px-4 py-3">
                    <div className="text-sm">
                      <span className="font-medium">Connected:</span> <span className="mono">{gm.email}</span>
                    </div>
                    {user?.role === "admin" && (
                      <button data-testid={SETTINGS.gmailDisconnect} onClick={disconnectGmail} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive">
                        <Unlink size={12} /> Disconnect
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="mt-4">
                    {user?.role === "admin" ? (
                      <button data-testid={SETTINGS.gmailConnect} onClick={connectGmail} disabled={working} className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
                        {working ? <Loader2 size={14} className="animate-spin" /> : <LinkIcon size={14} />} Connect Gmail
                      </button>
                    ) : (
                      <div className="text-sm text-muted-foreground">Only an admin can connect Gmail for this organization.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* CSV Exports */}
          <section className="border border-border bg-card rounded-md p-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-md bg-accent flex items-center justify-center">
                <FileDown size={18} className="text-primary" />
              </div>
              <div className="flex-1">
                <div className="serif text-lg text-primary">Accounting exports</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Download all non-deleted expenses as CSV, formatted for direct import.
                </p>
                <div className="mt-5 grid sm:grid-cols-3 gap-3">
                  <ExportCard
                    testId={SETTINGS.exportTrustdime}
                    title="TrustDime full"
                    body="All fields, including status, notes, payment refs."
                    onClick={() => download("trustdime")}
                  />
                  <ExportCard
                    testId={SETTINGS.exportQuickbooks}
                    title="QuickBooks Online"
                    body="Bank transaction format. Amounts as negative. Program as Class."
                    onClick={() => download("quickbooks")}
                  />
                  <ExportCard
                    testId={SETTINGS.exportAplos}
                    title="Aplos"
                    body="Date / Payee / Amount / Fund / Account / Memo."
                    onClick={() => download("aplos")}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* PWA hint */}
          <section className="border border-border bg-card rounded-md p-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-md bg-accent flex items-center justify-center">
                <span className="serif text-primary">A</span>
              </div>
              <div className="flex-1">
                <div className="serif text-lg text-primary">Install as a mobile app</div>
                <p className="text-sm text-muted-foreground mt-1">
                  TrustDime is a PWA. On mobile, use your browser&apos;s <em>Add to Home Screen</em>
                  &nbsp;to install. Receipt capture works natively from the camera.
                </p>
              </div>
            </div>
          </section>
        </div>
      </PageBody>
    </>
  );
}

function ExportCard({ title, body, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="text-left border border-border bg-background hover:bg-accent rounded-md p-4 transition-colors"
    >
      <div className="font-medium text-sm">{title}</div>
      <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{body}</div>
      <div className="mt-3 inline-flex items-center gap-1 text-xs text-primary">
        <FileDown size={12} /> Download
      </div>
    </button>
  );
}
